<template>
  <div class="content">
    <div class="container-fluid">
      <div class="row">
        <div class="col-md-12">
          <div class="card">
            <div class="card-header d-flex justify-content-between align-items-center">
              <h4 class="card-title">Rescheduled Event Sales</h4>
              <button @click="fetchData" class="btn btn-primary btn-round btn-icon shadow-sm" :disabled="isLoading">
                <i class="fas fa-sync-alt" :class="{ 'fa-spin': isLoading }"></i>
              </button>
            </div>
            <div class="card-body">
              <b-overlay :show="isLoading" rounded="sm">
                <vue-good-table
                  compactMode
                  styleClass="vgt-table condensed"
                  :columns="columns"
                  :rows="groupedSalesData"
                  :group-options="{
                    enabled: true,
                    collapsable: true
                  }"
                  :pagination-options="{
                    enabled: true,
                    perPage: 1000,
                    perPageDropdown: [50, 100, 200, 500, 1000]
                  }"
                  :search-options="{
                    enabled: true
                  }"
                >
                  <template slot="table-header-row" slot-scope="props">
                    <span class="font-weight-bold">
                      {{ props.row.label }}
                    </span>
                    <span v-if="props.row.children" class="event-count-badge ml-2">
                      {{ props.row.children.length }} Passes
                    </span>
                    <span class="ml-2 light-grey-text" style="font-size: 0.85rem;">
                      {{ props.row.venue }} • Rescheduled: {{ formatDate(props.row.rescheduled_date) }}
                    </span>
                  </template>
                  <template slot="table-row" slot-scope="props">
                    <span v-if="props.column.field === 'listPrice' || props.column.field === 'profit'">
                      ${{ props.formattedRow[props.column.field] }}
                    </span>
                    <span v-else-if="props.column.field === 'rescheduled_date' || props.column.field === 'invoiceDate'">
                      {{ formatDate(props.row[props.column.field]) }}
                    </span>
                    <span v-else-if="props.column.field === 'primary_location_url'">
                      <a v-if="props.row.primary_location_url" :href="props.row.primary_location_url" target="_blank" class="btn btn-info btn-icon btn-round btn-sm shadow-sm" title="Parking Link">
                        <i class="fas fa-parking"></i>
                      </a>
                    </span>
                    <span v-else-if="props.column.field === 'pw_parking_pass_url'">
                      <a v-if="props.row.pw_parking_pass_url && String(props.row.pw_parking_pass_url).startsWith('http')" :href="props.row.pw_parking_pass_url" target="_blank" class="btn btn-primary btn-icon btn-round btn-sm shadow-sm" title="Pass Link">
                        <i class="fas fa-ticket-alt"></i>
                      </a>
                    </span>
                    <span v-else-if="props.column.field === 'external_invoice_ref'">
                      {{ props.formattedRow[props.column.field] }}
                      <i class="far fa-copy ml-1 text-primary cursor-pointer" @click.stop="copyToClipboard(props.row.external_invoice_ref)" title="Copy Ref #"></i>
                    </span>
                    <span v-else>
                      {{ props.formattedRow[props.column.field] }}
                    </span>
                  </template>
                </vue-good-table>
              </b-overlay>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import 'vue-good-table/dist/vue-good-table.css'
import { VueGoodTable } from 'vue-good-table';
import axios from 'axios';
import { format } from 'date-fns';

export default {
  name: 'RescheduledEventSales',
  components: {
    VueGoodTable,
  },
  data() {
    return {
      isLoading: false,
      salesData: [],
      columns: [
        { label: "Venue", field: "venue_name", hidden: true },
        { label: "Event", field: "event_name", hidden: true },
        { label: "Rescheduled Date", field: "rescheduled_date", hidden: true },
        { label: "Invoice Date", field: "invoiceDate" },
        { label: "Ref #", field: "external_invoice_ref" },
        { label: "Primary", field: "primary_name" },
        { label: "Exchange", field: "exchange" },
        { label: "Price", field: "listPrice", type: 'number' },
        { label: "Profit", field: "profit", type: 'number' },
        { label: "Status", field: "sync_order_status" },
        { label: "", field: "primary_location_url", sortable: false, width: '50px' },
        { label: "", field: "pw_parking_pass_url", sortable: false, width: '50px' },
      ]
    }
  },
  computed: {
    groupedSalesData() {
      const groups = {};
      this.salesData.forEach(item => {
        const key = `${item.venue_name} - ${item.event_name}`;
        if (!groups[key]) {
          groups[key] = {
            label: item.event_name,
            venue: item.venue_name,
            rescheduled_date: item.rescheduled_date,
            mode: 'span',
            children: []
          };
        }
        groups[key].children.push(item);
      });
      return Object.values(groups);
    }
  },
  mounted() {
    this.fetchData();
  },
  methods: {
    async fetchData() {
      this.isLoading = true;
      try {
        const response = await axios.get('https://api.spotjockeys.com/api/rescheduled-event-sales');
        this.salesData = response.data;
      } catch (error) {
        console.error('Error fetching rescheduled sales data:', error);
        if (this.$toast) {
          this.$toast.error('Failed to fetch data');
        }
      } finally {
        this.isLoading = false;
      }
    },
    formatDate(dateString) {
      if (!dateString) return '';
      try {
        return format(new Date(dateString), 'MM/dd/yyyy HH:mm');
      } catch (e) {
        return dateString;
      }
    },
    async copyToClipboard(text) {
      if (!text) return;
      try {
        await navigator.clipboard.writeText(text);
        if (this.$toast) {
          this.$toast.success('Copied to clipboard');
        }
      } catch (err) {
        console.error('Failed to copy: ', err);
        if (this.$toast) {
          this.$toast.error('Failed to copy');
        }
      }
    }
  }
}
</script>

<style scoped>
.content {
  padding: 30px 15px;
}
.vgt-table {
  font-size: 13px !important;
}
.event-count-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 12px;
  background-color: #3498db;
  color: white;
  font-weight: bold;
  font-size: 11px;
}
.btn-icon {
  width: 32px;
  height: 32px;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.btn-round {
  border-radius: 50% !important;
}
.cursor-pointer {
  cursor: pointer;
}
.light-grey-text {
  color: #888;
}
</style>
